import React, { useState } from 'react';
import styled from 'styled-components';
import { COLOR_SYSTEM, Text, Button, Spinner } from 'components/atoms';
import { useCreateExperiment } from 'hooks/aiReviewTest/useExperiments';
import { CreateExperimentDto, TestConfiguration } from 'types/aiReviewTest';
import { PromptType } from 'common/models/types';
import ExperimentStepIndicator from './ExperimentStepIndicator';
import ExperimentStep1 from './ExperimentStep1';
import ExperimentStep2 from './ExperimentStep2';
import ExperimentStep3 from './ExperimentStep3';
import ExperimentStep4 from './ExperimentStep4';
import ExperimentStep5 from './ExperimentStep5';
import ExperimentStep7 from './ExperimentStep7';

interface ExperimentExecuteModalProps {
  onConfirm: (experiment: CreateExperimentDto) => Promise<void>;
  onCancel: () => void;
}

export interface StepData {
  // Step 1: 기본 정보
  name?: string;
  description?: string;
  
  // Step 2: 실행 모드
  modes?: {
    createMode: boolean;
    evaluateMode: boolean;
    reevaluateMode: boolean;
  };
  
  // Step 3: AI Review 생성 설정
  promptType?: PromptType;
  modelType?: string;
  promptId?: number;
  medicationOptions?: string[];
  docsOptions?: string[];
  
  // Step 4: 평가 설정
  evaluationPromptId?: number;
  evaluationModel?: string;
  evaluationUseGTSnapshot?: boolean;
  iterations?: number;
  
  // Step 5: ID 선택
  patientIds?: number[];
  aiReviewIds?: number[];
  aiReviewEvaluationIds?: number[];
  
  // 추가 옵션
  admin?: {
    rerun?: boolean;
    targetProcessingStep?: string;
  };
}

export interface ConfigurationData {
  id: string;
  config: TestConfiguration;
  isExpanded?: boolean;
}

const ExperimentExecuteModal: React.FC<ExperimentExecuteModalProps> = ({
  onConfirm,
  onCancel,
}) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [stepData, setStepData] = useState<StepData>({
    modes: {
      createMode: false,
      evaluateMode: false,
      reevaluateMode: false,
    },
    medicationOptions: [],
    docsOptions: [],
    iterations: 1,
  });
  
  const [configurations, setConfigurations] = useState<ConfigurationData[]>([]);
  const [isAddingConfig, setIsAddingConfig] = useState(false);
  
  const createExperimentMutation = useCreateExperiment();
  
  const handleStepComplete = (data: Partial<StepData>) => {
    setStepData(prev => ({ ...prev, ...data }));
    
    // Step 5에서 Step 6로 넘어갈 때 configuration 생성
    if (currentStep === 5) {
      const mergedData = { ...stepData, ...data };
      const newConfig: TestConfiguration = {
        aiReviewRequest: {
          modelType: mergedData.modelType,
          promptType: mergedData.promptType,
          promptId: mergedData.promptId,
          medicationOptions: mergedData.medicationOptions,
          docsOptions: mergedData.docsOptions,
          evaluationPromptId: mergedData.evaluationPromptId,
          evaluationModel: mergedData.evaluationModel,
          evaluationUseGTSnapshot: mergedData.evaluationUseGTSnapshot,
          admin: mergedData.admin,
        },
        patientIds: mergedData.patientIds,
        aiReviewIds: mergedData.aiReviewIds,
        aiReviewEvaluationIds: mergedData.aiReviewEvaluationIds,
        iterations: mergedData.iterations || 1,
      };
      
      if (isAddingConfig) {
        // 추가 모드인 경우 configurations에 추가
        setConfigurations(prev => [...prev, {
          id: Date.now().toString(),
          config: newConfig,
          isExpanded: false,
        }]);
        setIsAddingConfig(false); // 추가 모드 종료
      } else if (configurations.length === 0) {
        // 첫 번째 configuration인 경우
        setConfigurations([{
          id: Date.now().toString(),
          config: newConfig,
          isExpanded: false,
        }]);
      }
    }
    
    if (currentStep < 6) {
      setCurrentStep(currentStep + 1);
    }
  };
  
  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };
  
  const handleExecute = async () => {
    // configurations를 사용 (이미 Step 5에서 자동으로 추가됨)
    const finalConfigs = configurations.map(c => c.config);
    
    const experimentDto: CreateExperimentDto = {
      name: stepData.name,
      description: stepData.description,
      configuration: finalConfigs,
    };
    
    console.log('실험 생성 요청 데이터:', experimentDto);
    
    await onConfirm(experimentDto);
  };
  
  const handleAddConfiguration = () => {
    // stepData 초기화 (이름과 설명은 유지)
    setStepData({
      name: stepData.name,
      description: stepData.description,
      modes: {
        createMode: false,
        evaluateMode: false,
        reevaluateMode: false,
      },
      medicationOptions: [],
      docsOptions: [],
      iterations: 1,
    });
    
    setIsAddingConfig(true);
    setCurrentStep(2); // 실행 모드 선택부터 시작
  };
  
  const getTotalSteps = () => {
    // 모드에 따라 총 스텝 수가 달라질 수 있음
    return 6;
  };
  
  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return <ExperimentStep1 data={stepData} onNext={handleStepComplete} onCancel={onCancel} />;
      case 2:
        return <ExperimentStep2 data={stepData} onNext={handleStepComplete} onBack={handleBack} />;
      case 3:
        return <ExperimentStep3 data={stepData} onNext={handleStepComplete} onBack={handleBack} />;
      case 4:
        return <ExperimentStep4 data={stepData} onNext={handleStepComplete} onBack={handleBack} />;
      case 5:
        return <ExperimentStep5 data={stepData} onNext={handleStepComplete} onBack={handleBack} />;
      case 6:
        return <ExperimentStep7 
          data={stepData} 
          configurations={configurations}
          setConfigurations={setConfigurations}
          isAddingConfig={isAddingConfig}
          onAddConfiguration={handleAddConfiguration}
          onExecute={handleExecute} 
          onBack={handleBack} 
          isLoading={createExperimentMutation.isLoading} 
        />;
      default:
        return null;
    }
  };
  
  return (
    <Container>
      <Header>
        <Title>AI Review 자동화 테스트 실행</Title>
        <CloseButton onClick={onCancel}>✕</CloseButton>
      </Header>
      
      <StepIndicatorWrapper>
        <ExperimentStepIndicator currentStep={currentStep} totalSteps={getTotalSteps()} />
      </StepIndicatorWrapper>
      
      <Content>
        {renderStep()}
      </Content>
    </Container>
  );
};

const Container = styled.div`
  width: 900px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  background: white;
  border-radius: 12px;
  overflow: hidden;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid ${COLOR_SYSTEM.get("Gray")?.[200]};
`;

const Title = styled(Text)`
  font-size: 20px;
  font-weight: 600;
  color: ${COLOR_SYSTEM.get("Gray")?.[900]};
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  color: ${COLOR_SYSTEM.get("Gray")?.[400]};
  cursor: pointer;
  padding: 4px;
  
  &:hover {
    color: ${COLOR_SYSTEM.get("Gray")?.[600]};
  }
`;

const StepIndicatorWrapper = styled.div`
  padding: 24px 24px 0;
`;

const Content = styled.div`
  flex: 1;
  padding: 24px;
  overflow-y: auto;
`;

export default ExperimentExecuteModal;